import { Link } from "react-router-dom";
import { Plane, MessageCircle, ChevronRight, Shield, Zap, BookOpen } from "lucide-react";
import useSiteSettings from "@/hooks/useSiteSettings";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const settings = useSiteSettings();

  return (
    <footer className="relative border-t border-slate-800/50 bg-slate-950/50">
      {/* Top gradient line */}
      <div className="gradient-line" />
      
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6" data-testid="footer-logo">
              <div className="relative">
                <div className="w-12 h-12 bg-primary/10 border border-primary/30 rounded-lg flex items-center justify-center">
                  <Plane className="w-6 h-6 text-primary" />
                </div>
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full" />
              </div>
              <div>
                <h2 className="text-xl font-bold tracking-wider">AEROCORE</h2>
                <p className="text-[10px] tracking-[0.2em] text-muted-foreground -mt-1">SCRIPTS</p>
              </div>
            </Link>
            <p className="text-sm text-muted-foreground leading-relaxed mb-6">
              Premium quality aviation scripts for FiveM servers. 
              Realistic, optimized, and professional solutions.
            </p>
            {settings?.discord_url && (
              <a
                href={settings.discord_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-discord"
                className="w-10 h-10 bg-slate-900 border border-slate-800 rounded-lg flex items-center justify-center hover:border-primary/50 hover:bg-slate-800 transition-colors"
                aria-label="Join AeroCore on Discord"
              >
                <MessageCircle className="w-4 h-4 text-muted-foreground" />
              </a>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
              Quick Links
            </h3>
            <ul className="space-y-3">
              {[
                { href: "/", label: "Home" },
                { href: "/products", label: "All Products" },
                { href: "https://docs.aerocorescripts.com", label: "Documentation", external: true },
                { href: "/about", label: "About Us" },
                { href: "/contact", label: "Contact" },
                { href: "/terms", label: "Terms" },
                { href: "/privacy", label: "Privacy" },
                { href: "/refund-policy", label: "Refund Policy" },
              ].map((link) => (
                <li key={link.href}>
                  {link.external ? (
                    <a
                      href={link.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-link-documentation"
                      className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group"
                    >
                      <ChevronRight className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                      {link.label}
                    </a>
                  ) : (
                    <Link
                      to={link.href}
                      data-testid={`footer-link-${link.href.replace("/", "") || "home"}`}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group"
                    >
                      <ChevronRight className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                      {link.label}
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Categories */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
              Categories
            </h3>
            <ul className="space-y-3">
              {[
                { href: "/products?category=helicopters", label: "Helicopter Scripts" },
                { href: "/products?category=aircraft", label: "Aircraft Systems" },
                { href: "/products?category=atc-systems", label: "ATC Systems" },
                { href: "/products?category=career-systems", label: "Career Systems" },
              ].map((link) => (
                <li key={link.href}>
                  <Link
                    to={link.href}
                    className="text-sm text-muted-foreground hover:text-primary transition-colors flex items-center gap-2 group"
                  >
                    <ChevronRight className="w-3 h-3 text-primary opacity-0 group-hover:opacity-100 transition-opacity" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Trust Badges */}
          <div>
            <h3 className="text-xs uppercase tracking-widest text-muted-foreground mb-6">
              Secure Shopping
            </h3>
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Shield className="w-5 h-5 text-primary" />
                <span>Tebex Secure</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Zap className="w-5 h-5 text-primary" />
                <span>Delivery through Tebex</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <BookOpen className="w-5 h-5 text-primary" />
                <span>Product documentation</span>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800/50">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              {currentYear} AeroCore Scripts. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              <span className="text-xs text-muted-foreground">
                Premium Aviation Scripts for FiveM
              </span>
              <span className="text-xs text-muted-foreground">
                Powered by{" "}
                <a
                  href="https://webouse.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-primary hover:text-accent transition-colors"
                  data-testid="footer-webouse-link"
                >
                  Webouse
                </a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
